import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getLatestArticles } from '@/mocks/articles';

const relatedArticles = getLatestArticles(3);

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || 'article-1');

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          image: article.image,
          author: { '@type': 'Organization', name: article.author },
          datePublished: article.date,
          publisher: { '@type': 'Organization', name: '乐鱼官网' },
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                    {article.categoryName}
                  </span>
                  {article.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    阅读时长 {article.readTime}
                  </span>
                </div>
              </div>

              <div className="relative h-[300px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center flex-wrap gap-2 mt-10 pt-8 border-t border-background-200">
                <span className="text-sm text-foreground-500 font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-10 pt-8 border-t border-background-200">
                <Link
                  to={`/category/${article.category}`}
                  className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{article.categoryName}
                </Link>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="分享到微信">
                    <i className="ri-wechat-line text-foreground-600"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="分享到微博">
                    <i className="ri-weibo-line text-foreground-600"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="复制链接">
                    <i className="ri-link-m text-foreground-600"></i>
                  </a>
                </div>
              </div>
            </div>
          </article>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.filter((a) => a.id !== article.id).slice(0, 3).map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative h-40 md:h-44 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}